// src/components/intake/FileDrop.jsx
//
// One upload box: drop a file on it or click to choose one. The chosen file
// sits inside the box with its size and a Remove button, as in the mock-up.
// The choosing control and Remove are siblings rather than nested, so neither
// is a button inside a button.

import React, { useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { formatBytes } from '../../utils/intake';
import { SP, FONT, labelSx } from './intakeSx';

export default function FileDrop({ kind, label, hint, accept, file, error, onFile, onRemove, keepNote, detail }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const take = (list) => {
    const chosen = list && list[0];
    if (chosen) onFile(chosen);
  };

  return (
    <Box>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer.files);
        }}
        sx={{
          border: `1.5px dashed ${error ? tokens.color.error : over ? SP.purple : SP.lineSoft}`,
          borderRadius: '7px',
          background: over ? SP.lavender : 'transparent',
          transition: 'border-color .18s ease, background .18s ease',
          '&:hover': { borderColor: error ? tokens.color.error : SP.purple, background: SP.lavender },
          p: '6px',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          data-testid={`intake-drop-${kind}`}
          sx={{
            display: 'block',
            width: '100%',
            border: 0,
            background: 'transparent',
            font: 'inherit',
            color: SP.black,
            cursor: 'pointer',
            textAlign: 'center',
            p: '14px 10px',
            borderRadius: '5px',
            '&:focus-visible': { outline: `2px solid ${SP.purpleSoft}`, outlineOffset: 1 },
          }}
        >
          <Box sx={labelSx}>{label}</Box>
          <Typography sx={{ color: SP.gray, fontSize: 11.5, mt: '5px' }}>{hint}</Typography>
          <Typography sx={{ color: SP.purple, fontSize: 12, mt: '8px', fontWeight: 600 }}>
            {file ? 'Choose a different file' : 'Choose a file or drop it here'}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          data-testid={`intake-input-${kind}`}
          onChange={(e) => {
            take(e.target.files);
            // Cleared so choosing the same file again after Remove still fires.
            e.target.value = '';
          }}
        />
        {file && (
          <Box
            sx={{
              m: '4px',
              background: SP.lavender,
              borderRadius: '5px',
              p: '9px 11px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflowWrap: 'anywhere' }}>{file.name}</Typography>
              <Typography sx={{ fontFamily: FONT.mono, fontSize: 10.5, color: SP.gray, mt: '2px' }}>
                {formatBytes(file.size)}
              </Typography>
              {detail && (
                <Typography sx={{ fontSize: 11.5, color: SP.gray, mt: '2px' }} data-testid={`intake-detail-${kind}`}>
                  {detail}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              onClick={onRemove}
              sx={{ color: tokens.color.error, textTransform: 'none', fontFamily: FONT.mono, fontSize: 11, flexShrink: 0 }}
            >
              Remove
            </Button>
          </Box>
        )}
        {!file && keepNote && (
          <Typography sx={{ fontSize: 11.5, color: SP.gray, textAlign: 'center', pb: '8px' }}>{keepNote}</Typography>
        )}
      </Box>
      {error && (
        <Typography role="alert" sx={{ color: tokens.color.error, fontSize: 11.5, mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
